import React, { useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";
import { useForm } from "react-hook-form";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { SearchSelectMulti } from "../../../components/common/form/SearchSelect/SearchSelectMulti";
import {
  Course,
  useAddPreRequisitesToCourseMutation,
  useGetCoursesBySchoolByQueryQuery,
} from "../../../graphql/generated/graphql";
import { useAppConfig } from "../../../AppConfigProvider";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import NewCustomAxioActionModal from "../../settings/UsersTabContainer/NewCustomAxioActionModal";
import CoursePreRequisitePill from "./CoursePreRequisitePill";

const AddCoursePrerequisitesModal = ({
  visible,
  onClose,
  course,
  refetchCourse,
}: {
  visible: boolean;
  onClose: () => void;
  course: Course;
  refetchCourse: () => void;
}) => {
  const { isMedium } = useResponsiveScreen();
  const [searchQuery, setSearchQuery] = useState("");
  const { primaryColor, axioSchools } = useAppConfig();

  const schoolId = axioSchools[0];

  const [addPrerequisites] = useAddPreRequisitesToCourseMutation();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      preRequisites: [],
    },
  });

  const {
    data: coursesData,
    refetch: coursesRefetch,
    loading,
  } = useGetCoursesBySchoolByQueryQuery({
    variables: {
      schoolId,
      page: 1,
      pageSize: 100,
    },
  });

  useEffect(() => {
    coursesRefetch();
  }, [searchQuery]);

  const onSubmit = async (data: { preRequisites: { id: string; name: string }[] }) => {
    const { data: addPrerequisitesData } = await addPrerequisites({
      variables: {
        courseId: course.id,
        preRequisiteIds: data.preRequisites.map((prerequisite) => prerequisite.id),
      },
    });
    reset();
    refetchCourse();
  };

  return (
    <NewCustomAxioActionModal
      visible={visible}
      onClose={onClose}
      modalTexts={{
        title: "Add Course Prerequisites",
        actionButtonText: "Add",
        ariaLabel: "Add course prerequisites modal",
        warningText: "",
      }}
      onAction={handleSubmit(onSubmit)}>
      <View style={styles.formGroup}>
        <CustomText
          text="Course Prerequisites"
          size="s"
          textType="text"
          style={{ color: "#182230" }}
          weight="ultraBold"
        />
        {course.preRequisites.length > 0 && (
          <View style={{ flexDirection: "row", gap: 12, marginVertical: 12, flexWrap: "wrap" }}>
            {course.preRequisites
              ?.filter((prerequisite) => prerequisite.id !== course.id)
              .map((prerequisite) => (
                <CoursePreRequisitePill
                  key={prerequisite.id}
                  courseId={course.id}
                  prerequisite={prerequisite}
                  refetchCourse={refetchCourse}
                />
              ))}
          </View>
        )}
        <SearchSelectMulti
          name="preRequisites"
          control={control}
          label=""
          loading={loading}
          placeholder="Start typing to search or select from the list below"
          backgroundColor={primaryColor}
          showAllByDefault={true}
          height={200}
          itemType="course"
          showChips={false}
          fetchOptions={async (searchQuery) => {
            setSearchQuery(searchQuery);

            return (
              coursesData?.getCoursesBySchoolByQuery?.data.courses
                .filter((d) => d.id !== course.id)
                .filter((d) => !course.preRequisites.some((p) => p.id === d.id))
                .map((d) => ({
                  id: d.id,
                  name: d.name,
                })) ?? []
            );
          }}
        />
      </View>
    </NewCustomAxioActionModal>
  );
};

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: 24,
  },
});

export default AddCoursePrerequisitesModal;
